import { FeatureFlag } from '@mews-commander/enums';
import { TooltippedSpan } from '@mews-commander/ui';
import { lt, useFeatureFlag } from '@mews/framework';
import { Divider, DropDownButton, DropdownItem, IconButton, IconName, Stack } from '@optimus-web/core';
import { useCentralProductForm } from '../../hooks/useCentralProductForm';
import { useDeleteCentralProductDialog } from '../../hooks/useDeleteCentralProductDialog';
import { useHandleAssignProperty } from '../../hooks/useHandleAssignProperty';
import { AddProductToPropertyDialog } from '../_shared/AddProductToPropertyDialog';
import { CentralProductForm } from '../CentralProductForm/CentralProductForm';

interface Props {
    id: string;
    assignedPropertyIds: string[];
    closeSidePanel: () => void;
}

export const CentralProductSidePanelActions = ({ id, assignedPropertyIds, closeSidePanel }: Props) => {
    const centralProductsEditEnabled = useFeatureFlag(FeatureFlag.centralProductsEditEnabled);

    const {
        states: updateCentralProductStates,
        handlers: updateCentralProductHandlers,
        isPending,
        formErrors,
        handleSubmit,
    } = useCentralProductForm({ centralProductId: id });

    const {
        handleAddToProperty,
        handleConfirm,
        handlers: assignHandlers,
        renderConfigureProductDialog,
        states: assignStates,
    } = useHandleAssignProperty(id);

    const { confirmationDialog, openConfirmationDialog } = useDeleteCentralProductDialog({
        centralProductIds: [id],
        propertyIdsLength: assignedPropertyIds.length,
        onSuccess: closeSidePanel,
    });

    return (
        <Stack spacing="100">
            {centralProductsEditEnabled && (
                <TooltippedSpan tooltip={lt('Edit')}>
                    <IconButton
                        size="small"
                        icon={IconName.Edit}
                        type="button"
                        onClick={updateCentralProductHandlers.handleOpen}
                    />
                </TooltippedSpan>
            )}
            <DropDownButton icon={IconName.More} size="small" variant="tertiary">
                <DropdownItem label={lt('AddToProperty')} onClick={handleAddToProperty} />
                <Divider />
                <DropdownItem label={lt('Delete')} variant="danger" onClick={openConfirmationDialog} />
            </DropDownButton>
            {/* Edit central product */}
            {updateCentralProductStates.isDialogOpened && (
                <CentralProductForm
                    centralProductId={id}
                    isLoading={isPending}
                    active={updateCentralProductStates.isDialogOpened}
                    formErrors={formErrors}
                    onCancel={updateCentralProductHandlers.handleCancel}
                    onSubmit={handleSubmit}
                />
            )}
            {/* Add property to central product */}
            {assignStates.isDialogOpened && (
                <AddProductToPropertyDialog
                    onConfirm={handleConfirm}
                    onCancel={assignHandlers.handleCancel}
                    propertyIds={assignedPropertyIds}
                />
            )}
            {/* Configure properties to central product */}
            {renderConfigureProductDialog()}
            {/* Delete central product */}
            {confirmationDialog}
        </Stack>
    );
};
